import { FieldShell, SelectField, type SelectFieldOption } from '@today/ui-components'
import type { ChangeEvent } from 'react'

// The small, path-aware field controls the file-exchange form is built from
// (TO-391 REQ-015): each looks its error up by the API's field path, so a
// `details.fields` violation lands next to the field that caused it.

/** Field path (as the API reports it) → message to show next to that field. */
export type FieldErrors = Readonly<Record<string, string>>

const INPUT_CLASS =
	'h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50'

interface BaseProps {
	label: string
	path: string
	errors: FieldErrors
}

export function TextInput({
	label,
	path,
	errors,
	value,
	onChange,
	placeholder,
}: BaseProps & { value: string; onChange: (value: string) => void; placeholder?: string }) {
	return (
		<FieldShell label={label} error={errors[path]}>
			<input
				type="text"
				className={INPUT_CLASS}
				value={value}
				placeholder={placeholder}
				onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
			/>
		</FieldShell>
	)
}

export function NumberInput({
	label,
	path,
	errors,
	value,
	onChange,
}: BaseProps & { value: number; onChange: (value: number) => void }) {
	return (
		<FieldShell label={label} error={errors[path]}>
			<input
				type="number"
				className={INPUT_CLASS}
				value={Number.isFinite(value) ? value : ''}
				onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(Number(event.target.value))}
			/>
		</FieldShell>
	)
}

export function Choice<T extends string>({
	label,
	path,
	errors,
	value,
	options,
	onChange,
}: BaseProps & { value: T; options: SelectFieldOption[]; onChange: (value: T) => void }) {
	return (
		<SelectField
			label={label}
			options={options}
			value={value}
			error={errors[path]}
			onValueChange={(next) => onChange(next as T)}
		/>
	)
}

export function CheckboxInput({
	label,
	checked,
	onChange,
}: {
	label: string
	checked: boolean
	onChange: (checked: boolean) => void
}) {
	return (
		<label className="flex items-center gap-2 text-ds-sm text-ds-text">
			<input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
			{label}
		</label>
	)
}

/** An error that belongs to a group of controls rather than one input. */
export function GroupError({ path, errors }: { path: string; errors: FieldErrors }) {
	const message = errors[path]
	return message ? <p className="text-ds-sm text-ds-danger">{message}</p> : null
}
